import { Box } from '@material-ui/core';
import { PageLoader, SEmptyState, SHeader } from '@setvi/shared/components';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { ProductActionType } from 'pages/products/utils';
import { Product, ProductDetailsTabsIds } from '@setvi/shared/interfaces';
import {
  AnalyticsAppType,
  AnalyticsModules,
  useAnalytics
} from '@setvi/shared/hooks';
import { DetailsContent } from 'componentsV2/product/details/content';
import { useProductDetailsPage } from './useProductDetailsPage';

export enum SearchParams {
  DEFAULT_OPENED_TAB = 'defaultOpenedTab',
  SELECTED_CUSTOMER_ID = 'selectedCustomerId',
  APP_TYPE = 'appType'
}

const ProductDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const [defaultOpenedTab] = useQueryParam(
    SearchParams.DEFAULT_OPENED_TAB,
    StringParam
  );
  const [selectedCustomerId] = useQueryParam(
    SearchParams.SELECTED_CUSTOMER_ID,
    StringParam
  );
  const [appType] = useQueryParam(SearchParams.APP_TYPE, StringParam);
  const {
    client,
    customer,
    isLoading,
    product,
    fetchCustomer,
    fetchProduct,
    updateProduct
  } = useProductDetailsPage();
  const location = useLocation();
  const navigate = useNavigate();
  const { captureAnalytics } = useAnalytics();

  useEffect(() => {
    if (id) fetchProduct(id);
    if (selectedCustomerId) fetchCustomer(selectedCustomerId);
  }, [fetchProduct, fetchCustomer, id, selectedCustomerId]);

  const handleProductDetails = (newProduct: Product) => {
    const newPathname = location.pathname.replace(id, newProduct._id);
    const searchParams = new URLSearchParams(location.search);

    searchParams.set(
      SearchParams.DEFAULT_OPENED_TAB,
      ProductDetailsTabsIds.PRODUCT_INFO
    );

    navigate(
      {
        pathname: newPathname,
        search: searchParams.toString()
      },
      { replace: true }
    );
  };

  if (isLoading) return <PageLoader />;

  return (
    <Box height="100%" overflow="auto">
      <SHeader title="Product Details" />
      {product ? (
        <Box>
          <DetailsContent
            client={client}
            product={product}
            defaultOpenedTab={defaultOpenedTab as ProductDetailsTabsIds}
            selectedCustomer={customer}
            openProductDetails={handleProductDetails}
            readOnly
            captureResourceAnalytics={() =>
              captureAnalytics(
                {
                  appType: appType as AnalyticsAppType,
                  actionType: ProductActionType.AssetView,
                  productSKU: product.sku
                },
                AnalyticsModules.Catalog
              )
            }
            updateProduct={(_, newValues) => updateProduct(newValues)}
          />
        </Box>
      ) : (
        <SEmptyState
          title="Product Not Found"
          subTitle="There is no product found with specified ID."
        />
      )}
    </Box>
  );
};

export default ProductDetailsPage;
